import { recordDebugDiagnosticEvent } from '@/app/providers/labs/diagnostics'

export const CLIENT_REQUEST_CACHE_FOCUS_EXPIRY_MS = 60_000

type BaseClientRequestCacheValue<T> = {
  value: T
  expiresAt: number
}

type BaseClientRequestCacheEntry<T> = {
  pendingRequest?: Promise<T>
  cachedValue?: BaseClientRequestCacheValue<T>
}

type BaseClientRequestCacheFreshValue<T> =
  | { hasFreshValue: false }
  | { value: T; hasFreshValue: true }

type BaseClientRequestCacheRequestOptions<T> = {
  key: string
  request: () => Promise<T>
}

export type ClientRequestCacheKeyValue =
  | null
  | Date
  | string
  | number
  | boolean
  | undefined
  | ClientRequestCacheKeyValue[]
  | { [key: string]: ClientRequestCacheKeyValue }

export type BaseClientRequestCacheParams = {
  now?: () => number
  focusExpiryMs?: number
  getTtlMs?: (key: string) => number
  baseScope?: ClientRequestCacheKeyValue[]
}

type BaseClientRequestCacheStats = {
  hits: number
  misses: number
  deduped: number
  invalidations: number
}

/**
 * Coordinates client-side reads that often fire from multiple stores at the same time.
 * `dedupe` only shares the in-flight promise.
 * `cached` also stores a resolved value for its configured TTL.
 * Use `dedupe` for streamed data.
 * After the initial snapshot, streams become the source of truth.
 */
export class BaseClientRequestCache {
  #stats: BaseClientRequestCacheStats = {
    hits: 0,
    misses: 0,
    deduped: 0,
    invalidations: 0
  }
  #entries = new Map<string, BaseClientRequestCacheEntry<unknown>>()
  #baseScope: ClientRequestCacheKeyValue[]
  #getTtlMs: (key: string) => number
  #hiddenAt: number | undefined
  #focusExpiryMs: number
  #now: () => number

  constructor(options: BaseClientRequestCacheParams = {}) {
    this.#now = options.now || Date.now
    this.#baseScope = options.baseScope ?? []
    this.#getTtlMs =
      options.getTtlMs ??
      ((key) => {
        throw new Error(`Missing client request cache TTL for ${key}`)
      })
    this.#focusExpiryMs =
      options.focusExpiryMs ?? CLIENT_REQUEST_CACHE_FOCUS_EXPIRY_MS
  }

  handleVisibilityChange(isHidden: boolean) {
    if (isHidden) {
      this.#hiddenAt = this.#now()
      this.#recordVisibilityDiagnostic('Client request cache hidden')

      return
    }

    const hiddenDurationMs = this.#getHiddenDurationMs()
    const entryCountBeforeInvalidation = this.#entries.size
    const isExpired =
      hiddenDurationMs !== undefined && hiddenDurationMs >= this.#focusExpiryMs

    if (isExpired) {
      this.invalidate()
    }

    this.#recordVisibilityDiagnostic('Client request cache visible', {
      hiddenDurationMs,
      didInvalidate: isExpired,
      entryCountBeforeInvalidation,
      entryCountAfterInvalidation: this.#entries.size
    })
    this.#hiddenAt = undefined
  }

  cached<T>({ key, request }: BaseClientRequestCacheRequestOptions<T>) {
    const ttlMs = this.#getTtlMs(key)
    const freshValue = this.#getFreshValue<T>(key)

    if (freshValue.hasFreshValue) {
      this.#stats.hits += 1

      return Promise.resolve(freshValue.value)
    }

    const pendingRequest = this.#getPendingRequest<T>(key)

    if (pendingRequest) {
      return pendingRequest
    }

    return this.#startCachedRequest({ key, request }, ttlMs)
  }

  dedupe<T>(key: string, request: () => Promise<T>) {
    const pendingRequest = this.#getPendingRequest<T>(key)

    if (pendingRequest) {
      return pendingRequest
    }

    return this.#startDedupeRequest({ key, request })
  }

  invalidateByPrefix(prefix: string) {
    this.#stats.invalidations += 1

    for (const key of this.#entries.keys()) {
      if (key.startsWith(prefix)) {
        this.#entries.delete(key)
      }
    }
  }

  invalidate(key?: string) {
    this.#stats.invalidations += 1

    if (!key) {
      this.#entries.clear()

      return
    }

    this.#entries.delete(key)
  }

  createRequestKey(key: string, ...scope: ClientRequestCacheKeyValue[]) {
    return createBaseRequestKey(key, ...this.#baseScope, ...scope)
  }

  resetStats() {
    this.#stats = {
      hits: 0,
      misses: 0,
      deduped: 0,
      invalidations: 0
    }
  }

  has(key: string) {
    return this.#entries.has(key)
  }

  getStats() {
    return { ...this.#stats }
  }

  #startCachedRequest<T>(
    { key, request }: BaseClientRequestCacheRequestOptions<T>,
    ttlMs: number
  ) {
    this.#stats.misses += 1

    const pendingRequest = Promise.resolve()
      .then(request)
      .then((value) => {
        this.#storeCachedValue({
          key,
          value,
          ttlMs,
          pendingRequest
        })

        return value
      })
      .finally(() => {
        this.#clearPendingRequest(key, pendingRequest)
      })

    this.#setPendingRequest(key, pendingRequest)

    return pendingRequest
  }

  #storeCachedValue<T>({
    key,
    ttlMs,
    value,
    pendingRequest
  }: {
    value: T
    key: string
    ttlMs: number
    pendingRequest: Promise<T>
  }) {
    const entry = this.#getEntry<T>(key)

    if (entry?.pendingRequest !== pendingRequest) {
      return
    }

    this.#entries.set(key, {
      ...entry,
      cachedValue: {
        value,
        expiresAt: this.#now() + ttlMs
      }
    })
  }

  #startDedupeRequest<T>({
    key,
    request
  }: BaseClientRequestCacheRequestOptions<T>) {
    this.#stats.misses += 1

    const pendingRequest = Promise.resolve()
      .then(request)
      .finally(() => {
        this.#clearPendingRequest(key, pendingRequest)
      })

    this.#entries.set(key, {
      ...this.#getEntry<T>(key),
      pendingRequest
    })

    return pendingRequest
  }

  #clearPendingRequest<T>(key: string, pendingRequest: Promise<T>) {
    const entry = this.#getEntry<T>(key)

    if (entry?.pendingRequest !== pendingRequest) {
      return
    }

    if (entry.cachedValue) {
      this.#entries.set(key, {
        cachedValue: entry.cachedValue
      })

      return
    }

    this.#entries.delete(key)
  }

  #getFreshValue<T>(key: string): BaseClientRequestCacheFreshValue<T> {
    const cachedValue = this.#getEntry<T>(key)?.cachedValue

    if (!cachedValue || cachedValue.expiresAt <= this.#now()) {
      return { hasFreshValue: false }
    }

    return {
      value: cachedValue.value,
      hasFreshValue: true
    }
  }

  #recordVisibilityDiagnostic(
    message: string,
    attributes: Record<string, unknown> = {}
  ) {
    recordDebugDiagnosticEvent({
      message,
      source: 'cache',
      attributes: {
        ...attributes,
        entryCount: this.#entries.size
      }
    })
  }

  #getPendingRequest<T>(key: string) {
    const pendingRequest = this.#getEntry<T>(key)?.pendingRequest

    if (pendingRequest) {
      this.#stats.deduped += 1

      return pendingRequest
    }

    return undefined
  }

  #setPendingRequest<T>(key: string, pendingRequest: Promise<T>) {
    this.#entries.set(key, {
      ...this.#getEntry<T>(key),
      pendingRequest
    })
  }

  #getHiddenDurationMs() {
    if (this.#hiddenAt === undefined) {
      return undefined
    }

    return Math.max(this.#now() - this.#hiddenAt, 0)
  }

  #getEntry<T>(key: string) {
    return this.#entries.get(key) as undefined | BaseClientRequestCacheEntry<T>
  }
}

/**
 * Builds stable cache keys from arbitrary scope values.
 * Constructor-provided base scope is prepended by BaseClientRequestCache.createRequestKey.
 * Object key order and undefined fields are normalized before stringifying.
 */
export function createBaseRequestKey(
  key: string,
  ...scope: ClientRequestCacheKeyValue[]
) {
  return [key, JSON.stringify(scope.map(normalizeRequestKeyValue))].join(':')
}

export function getBaseRequestKey(requestKey: string) {
  return requestKey.split(':')[0]
}

function normalizeRequestKeyValue(
  value: ClientRequestCacheKeyValue
): Exclude<ClientRequestCacheKeyValue, undefined> {
  if (value === undefined) {
    return null
  }

  if (value === null || typeof value !== 'object') {
    return value
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (Array.isArray(value)) {
    return value.map(normalizeRequestKeyValue)
  }

  return Object.keys(value)
    .sort()
    .reduce(
      (normalized, key) => {
        const normalizedValue = normalizeRequestKeyValue(value[key])

        if (normalizedValue === null && value[key] === undefined) {
          return normalized
        }

        normalized[key] = normalizedValue

        return normalized
      },
      {} as Record<string, Exclude<ClientRequestCacheKeyValue, undefined>>
    )
}
